import React, { useState, useEffect, useCallback, useRef } from 'react';
import '../styles/PhotoManager.css';

interface Photo {
  id: number;
  filename: string;
  original_name: string;
  sort_order: number;
  uploaded_at: string;
}

interface PhotoManagerProps {
  token: string;
  apiBase: string;
}

const PhotoManager: React.FC<PhotoManagerProps> = ({ token, apiBase }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedPhoto, setDraggedPhoto] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const touchDragId = useRef<number | null>(null);
  const photosRef = useRef<Photo[]>(photos);

  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  const fetchPhotos = useCallback(async () => {
    try {
      const response = await fetch(`${apiBase}/photos`);
      if (response.ok) {
        const data = await response.json();
        setPhotos(data);
      }
    } catch (err) {
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  const showMessage = (type: 'success' | 'error', message: string) => {
    if (type === 'success') {
      setSuccess(message);
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(message);
      setSuccess(null);
    }
  };

  const handleUpload = async (files: FileList) => {
    if (files.length === 0) return;

    setUploading(true);
    setError(null);

    let uploadedCount = 0;
    const totalFiles = files.length;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('photo', file);

      try {
        const response = await fetch(`${apiBase}/photos/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        });

        if (response.ok) {
          uploadedCount++;
        } else {
          const data = await response.json();
          showMessage('error', data.error || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        showMessage('error', `Failed to upload ${file.name}`);
      }
    }

    if (uploadedCount > 0) {
      showMessage('success', `Successfully uploaded ${uploadedCount} of ${totalFiles} photos`);
      fetchPhotos();
    }

    setUploading(false);
  };

  const handleDelete = async (photoId: number) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      const response = await fetch(`${apiBase}/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        showMessage('success', 'Photo deleted successfully');
        setPhotos(photos.filter(p => p.id !== photoId));
      } else {
        const data = await response.json();
        showMessage('error', data.error || 'Failed to delete photo');
      }
    } catch (err) {
      showMessage('error', 'Failed to delete photo');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleUpload(e.target.files);
    }
  };

  // Reorder functionality
  const handlePhotoDragStart = (e: React.DragEvent, photoId: number) => {
    setDraggedPhoto(photoId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handlePhotoDragOver = (e: React.DragEvent, targetId: number) => {
    e.preventDefault();
    if (draggedPhoto === null || draggedPhoto === targetId) return;

    const draggedIndex = photos.findIndex(p => p.id === draggedPhoto);
    const targetIndex = photos.findIndex(p => p.id === targetId);

    if (draggedIndex === -1 || targetIndex === -1) return;

    const newPhotos = [...photos];
    const [removed] = newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(targetIndex, 0, removed);
    setPhotos(newPhotos);
  };

  const handlePhotoDragEnd = async () => {
    if (draggedPhoto === null) return;

    setDraggedPhoto(null);

    const photoIds = photos.map(p => p.id);

    try {
      const response = await fetch(`${apiBase}/photos/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ photoIds })
      });

      if (response.ok) {
        showMessage('success', 'Photo order saved');
      } else {
        showMessage('error', 'Failed to save order');
        fetchPhotos();
      }
    } catch (err) {
      showMessage('error', 'Failed to save order');
      fetchPhotos();
    }
  };

  // Touch drag-and-drop for mobile
  const handleTouchDragStart = (e: React.TouchEvent, photoId: number) => {
    touchDragId.current = photoId;
    setDraggedPhoto(photoId);
  };

  const handleTouchDragMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (touchDragId.current === null) return;

    const touch = e.touches[0];
    const element = document.elementFromPoint(touch.clientX, touch.clientY);
    const card = element?.closest('[data-photo-id]') as HTMLElement | null;
    if (!card) return;

    const targetId = parseInt(card.dataset.photoId || '');
    if (isNaN(targetId) || targetId === touchDragId.current) return;

    const current = photosRef.current;
    const draggedIndex = current.findIndex(p => p.id === touchDragId.current);
    const targetIndex = current.findIndex(p => p.id === targetId);
    if (draggedIndex === -1 || targetIndex === -1) return;

    const newPhotos = [...current];
    const [removed] = newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(targetIndex, 0, removed);
    setPhotos(newPhotos);
  };

  const handleTouchDragEnd = async () => {
    if (touchDragId.current === null) return;
    touchDragId.current = null;
    setDraggedPhoto(null);

    const photoIds = photosRef.current.map(p => p.id);
    try {
      const response = await fetch(`${apiBase}/photos/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ photoIds })
      });
      if (response.ok) {
        showMessage('success', 'Photo order saved');
      } else {
        showMessage('error', 'Failed to save order');
        fetchPhotos();
      }
    } catch (err) {
      showMessage('error', 'Failed to save order');
      fetchPhotos();
    }
  };

  const getImageUrl = (filename: string) => {
    return `${apiBase}/uploads/thumb_${filename}`;
  };

  if (loading) {
    return (
      <div className="photo-manager loading">
        <div className="loading-spinner" />
        <p>Loading photos...</p>
      </div>
    );
  }

  return (
    <div className="photo-manager">
      <div className="section-header">
        <h2>Photo Gallery</h2>
        <p>Drag photos to reorder. Click delete to remove.</p>
      </div>

      {(error || success) && (
        <div className={`message ${error ? 'error' : 'success'}`}>
          {error || success}
        </div>
      )}

      <div
        className={`upload-zone ${isDragging ? 'dragging' : ''} ${uploading ? 'uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          disabled={uploading}
        />
        {uploading ? (
          <>
            <div className="loading-spinner" />
            <p>Uploading photos...</p>
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <p>Drag photos here or click to upload</p>
            <span>Supports JPEG, PNG, WebP</span>
          </>
        )}
      </div>

      {photos.length === 0 ? (
        <div className="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <p>No photos yet</p>
          <span>Upload photos to get started</span>
        </div>
      ) : (
        <div className="photos-grid">
          {photos.map((photo) => (
            <div
              key={photo.id}
              data-photo-id={photo.id}
              className={`photo-card ${draggedPhoto === photo.id ? 'dragging' : ''}`}
              draggable
              onDragStart={(e) => handlePhotoDragStart(e, photo.id)}
              onDragOver={(e) => handlePhotoDragOver(e, photo.id)}
              onDragEnd={handlePhotoDragEnd}
              onTouchStart={(e) => handleTouchDragStart(e, photo.id)}
              onTouchMove={handleTouchDragMove}
              onTouchEnd={handleTouchDragEnd}
            >
              <div className="photo-image">
                <img src={getImageUrl(photo.filename)} alt={photo.original_name} />
                <div className="photo-overlay">
                  <button
                    className="delete-button"
                    onClick={() => handleDelete(photo.id)}
                    title="Delete photo"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                      <line x1="10" y1="11" x2="10" y2="17" />
                      <line x1="14" y1="11" x2="14" y2="17" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="photo-info">
                <span className="photo-name" title={photo.original_name}>
                  {photo.original_name}
                </span>
                <span className="drag-hint">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="8" y1="6" x2="16" y2="6" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                    <line x1="8" y1="18" x2="16" y2="18" />
                  </svg>
                  Drag to reorder
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PhotoManager;
